import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatINRFull, getStatusColor, formatStatus, LEAD_STATUSES, LEAD_CATEGORIES, formatDate } from '@/lib/utils';
import { Plus, Search, Filter, Grid3X3, List, ChevronRight, Trash2 } from 'lucide-react';
import { Pagination } from '@/components/Pagination';

const PIPELINE_STAGES = ['NEW', 'INITIAL_QUOTE', 'REVISED_QUOTE', 'FINAL_BID', 'NEGOTIATION', 'WON', 'LOST', 'ON_HOLD', 'CANCELLED', 'OBSOLETE'];
const PAGE_SIZE = 50;

export default function SalesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchLeads = (p = page) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    params.set('skip', ((p - 1) * PAGE_SIZE).toString());
    params.set('limit', PAGE_SIZE.toString());
    api.get(`/sales?${params}`).then(r => { setLeads(r.data.leads || []); setTotalCount(r.data.total_count || 0); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeads(page); }, [search, statusFilter, categoryFilter, page]);
  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data.clients || []));
    if (['DIRECTOR', 'SALES'].includes(user?.role)) api.get('/users').then(r => setUsers(r.data.users || []));
  }, [user]);

  const canCreate = ['DIRECTOR', 'SALES'].includes(user?.role);
  const canDelete = user?.role === 'DIRECTOR';

  const deleteLead = async (e, leadId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try { await api.delete(`/sales/${leadId}`); fetchLeads(); } catch (err) { alert('Delete failed'); }
  };

  return (
    <div data-testid="sales-pipeline-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Sales Pipeline</h1>
          <p className="text-sm text-slate-500">{totalCount} leads in pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-slate-200 rounded-md overflow-hidden">
            <button onClick={() => setView('list')} data-testid="view-list-btn"
              className={`px-3 py-1.5 text-sm ${view === 'list' ? 'bg-[#1B5486] text-white' : 'bg-white text-slate-600'}`}>
              <List size={16} />
            </button>
            <button onClick={() => setView('kanban')} data-testid="view-kanban-btn"
              className={`px-3 py-1.5 text-sm ${view === 'kanban' ? 'bg-[#1B5486] text-white' : 'bg-white text-slate-600'}`}>
              <Grid3X3 size={16} />
            </button>
          </div>
          {canCreate && (
            <button onClick={() => setShowCreateModal(true)} data-testid="create-lead-button"
              className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
              <Plus size={16} /> New Lead
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="search-leads-input"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none">
          <option value="">All Statuses</option>
          {LEAD_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-testid="filter-category"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none">
          <option value="">All Categories</option>
          {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>
      ) : view === 'list' ? (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Lead Code</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Client</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Requirement</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Quoted Amt</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Department</th>
                  {canDelete && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/sales/${lead.id}`)} data-testid={`lead-row-${lead.lead_code}`}>
                    <td className="py-3 px-4 font-medium text-[#1B5486]">{lead.lead_code}</td>
                    <td className="py-3 px-4 text-slate-900">{lead.client_short_name || lead.client_name}</td>
                    <td className="py-3 px-4 text-slate-700 max-w-[250px] truncate">{lead.project_requirement}</td>
                    <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{formatStatus(lead.category)}</span></td>
                    <td className="py-3 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(lead.status)}`}>{formatStatus(lead.status)}</span></td>
                    <td className="py-3 px-4 text-right font-mono text-slate-700">{formatINRFull(lead.initial_quoted_amount)}</td>
                    <td className="py-3 px-4 text-slate-500">{lead.department}</td>
                    {canDelete && <td className="py-3 px-2"><button onClick={(e) => deleteLead(e, lead.id)} data-testid={`delete-lead-${lead.lead_code}`} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {leads.length === 0 && <div className="text-center py-12 text-slate-400">No leads found</div>}
            <Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </div>
        </div>
      ) : (
        /* Kanban View */
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {PIPELINE_STAGES.map(stage => {
            const stageLeads = leads.filter(l => l.status === stage);
            return (
              <div key={stage} className="min-w-[280px] max-w-[300px] flex-shrink-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(stage)}`}>{formatStatus(stage)}</span>
                  <span className="text-xs text-slate-400">{stageLeads.length}</span>
                </div>
                <div className="space-y-2">
                  {stageLeads.map(lead => (
                    <div key={lead.id} onClick={() => navigate(`/sales/${lead.id}`)}
                      data-testid={`kanban-card-${lead.lead_code}`}
                      className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:-translate-y-[2px] hover:shadow-sm transition-all duration-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-[#1B5486]">{lead.lead_code}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{formatStatus(lead.category)}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 mb-1 line-clamp-2">{lead.project_requirement}</p>
                      <p className="text-xs text-slate-500 mb-2">{lead.client_short_name || lead.client_name} | {lead.department}</p>
                      <p className="text-sm font-mono font-medium text-slate-700">{formatINRFull(lead.initial_quoted_amount)}</p>
                    </div>
                  ))}
                  {stageLeads.length === 0 && <div className="text-center py-8 text-xs text-slate-300 border border-dashed border-slate-200 rounded-lg">No leads</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Lead Modal */}
      {showCreateModal && <CreateLeadModal clients={clients} users={users} onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); fetchLeads(); }} />}
    </div>
  );
}

function CreateLeadModal({ clients, users, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_id: '', department: '', project_requirement: '', category: 'CCTV',
    initial_contact_name: '', initial_contact_date: new Date().toISOString().slice(0, 10),
    initial_quoted_amount: '', cost_to_company: '', assigned_to_id: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        initial_quoted_amount: form.initial_quoted_amount === '' ? 0 : parseFloat(form.initial_quoted_amount) || 0,
        cost_to_company: form.cost_to_company === '' ? 0 : parseFloat(form.cost_to_company) || 0,
      };
      await api.post('/sales', payload);
      onCreated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-lead-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Sales Lead</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required
              data-testid="lead-client-select"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none">
              <option value="">Select client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Department *</label>
              <input type="text" value={form.department} onChange={e => setForm({...form, department: e.target.value})} required
                data-testid="lead-location-input"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                data-testid="lead-category-select"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none">
                {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Project Requirement *</label>
            <textarea value={form.project_requirement} onChange={e => setForm({...form, project_requirement: e.target.value})} required rows={2}
              data-testid="lead-requirement-input"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
              <input type="text" value={form.initial_contact_name} onChange={e => setForm({...form, initial_contact_name: e.target.value})} required
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Date</label>
              <input type="date" value={form.initial_contact_date} onChange={e => setForm({...form, initial_contact_date: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quoted Amount <span className="text-xs font-normal text-slate-400">(Optional)</span></label>
              <input type="number" value={form.initial_quoted_amount} onChange={e => setForm({...form, initial_quoted_amount: e.target.value})}
                placeholder="Auto-synced from BOQ Client Total"
                data-testid="lead-amount-input"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none placeholder:text-slate-300" />
              <p className="text-[10px] text-slate-400 mt-0.5">Leave blank — auto-calculated from BOQ</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cost to Company <span className="text-xs font-normal text-slate-400">(Optional)</span></label>
              <input type="number" value={form.cost_to_company} onChange={e => setForm({...form, cost_to_company: e.target.value})}
                placeholder="Auto-synced from BOQ Pace Total"
                data-testid="lead-cost-input"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none placeholder:text-slate-300" />
              <p className="text-[10px] text-slate-400 mt-0.5">Leave blank — auto-calculated from BOQ</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To</label>
            <select value={form.assigned_to_id} onChange={e => setForm({...form, assigned_to_id: e.target.value})}
              data-testid="lead-assigned-to-select"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none">
              <option value="">Unassigned</option>
              {users.filter(u => ['DIRECTOR', 'SALES'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 text-slate-700 py-2 rounded-md text-sm hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-lead-button"
              className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Creating...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
